import React from 'react';
import { listAllDocumentTypeOptions } from '../../../../dms/taxonomy/legalDocumentTaxonomy';

interface ConfigFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';
const selectClass = inputClass;
const documentTypeOptions = listAllDocumentTypeOptions();

// ── 1. field_required ──
export const FieldRequiredForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Field Name</label>
      <input
        type="text"
        className={inputClass}
        value={(config.field as string) ?? ''}
        onChange={(e) => onChange({ ...config, field: e.target.value })}
        placeholder="e.g. description, assignee_id"
      />
    </div>
    <div>
      <label className={labelClass}>Error Message</label>
      <input
        type="text"
        className={inputClass}
        value={(config.error_message as string) ?? ''}
        onChange={(e) => onChange({ ...config, error_message: e.target.value })}
        placeholder="Field X is required"
      />
    </div>
  </div>
);

// ── 2. deadline_not_passed ──
export const DeadlineNotPassedForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Deadline Field</label>
      <input
        type="text"
        className={inputClass}
        value={(config.deadline_field as string) ?? ''}
        onChange={(e) => onChange({ ...config, deadline_field: e.target.value })}
        placeholder="e.g. due_date, filing_deadline"
      />
    </div>
  </div>
);

// ── 3. form_complete ──
export const FormCompleteForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — validates all required form fields are filled.
  </p>
);

// ── 4. document_exists ──
export const DocumentExistsForm: React.FC<ConfigFormProps> = ({ config, onChange }) => {
  const value = (config.document_type as string) || (config.doc_type as string) || '';

  return (
    <div className="space-y-2">
      <div>
        <label className={labelClass} htmlFor="playbook-validator-document-type">Document Type</label>
        <select
          id="playbook-validator-document-type"
          className={selectClass}
          value={value}
          onChange={(e) => onChange({ ...config, document_type: e.target.value, doc_type: e.target.value })}
        >
          <option value="">Any document</option>
          {documentTypeOptions.map((documentType) => (
            <option key={documentType.value} value={documentType.playbookEventKey}>
              {documentType.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ── 5. expression ──
export const ExpressionValidatorForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Expression</label>
      <textarea
        className={`${inputClass} font-mono`}
        rows={3}
        value={(config.expression as string) ?? ''}
        onChange={(e) => onChange({ ...config, expression: e.target.value })}
        placeholder="e.g. context.task.priority !== 'low'"
      />
    </div>
    <div>
      <label className={labelClass}>Error Message</label>
      <input
        type="text"
        className={inputClass}
        value={(config.error_message as string) ?? ''}
        onChange={(e) => onChange({ ...config, error_message: e.target.value })}
        placeholder="Validation failed"
      />
    </div>
  </div>
);

// ── 6. custom ──
export const CustomValidatorForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Validator Name</label>
      <input
        type="text"
        className={inputClass}
        value={(config.validator_name as string) ?? ''}
        onChange={(e) => onChange({ ...config, validator_name: e.target.value })}
        placeholder="e.g. custom_budget_check"
      />
    </div>
  </div>
);

// ── Validator Form Router ──
const VALIDATOR_FORMS: Record<string, React.FC<ConfigFormProps>> = {
  field_required: FieldRequiredForm,
  deadline_not_passed: DeadlineNotPassedForm,
  form_complete: FormCompleteForm,
  document_exists: DocumentExistsForm,
  expression: ExpressionValidatorForm,
  custom: CustomValidatorForm,
};

interface ValidatorConfigFormProps extends ConfigFormProps {
  validatorType: string;
}

export const ValidatorConfigForm: React.FC<ValidatorConfigFormProps> = ({ validatorType, ...rest }) => {
  const Form = VALIDATOR_FORMS[validatorType];
  if (!Form) return <p className="text-[10px] text-gray-400 italic">Unknown validator type: {validatorType}</p>;
  return <Form {...rest} />;
};
